import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  PROJECT_STATUS, 
  PROJECT_ACTIONS,
  CLEARANCE_STATUS 
} from '../constants/projectConstants';
import { 
  sanitizeProjectData, 
  sanitizeClearanceData, 
  validateStatusChange,
  validateClearanceData 
} from '../utils/projectValidation';
import { financialYearUtils } from '../utils/helpers';

// Project service for handling all project-related Firebase operations
export const projectService = {
  
  // Submit a new project (for both admin and department users)
  async submitProject(projectData, userId) {
    try {
      const sanitizedData = sanitizeProjectData(projectData);
      
      // Add metadata
      const projectToSubmit = {
        ...sanitizedData,
        status: PROJECT_STATUS.PENDING,
        financialYear: financialYearUtils.calculateFinancialYear(), // Auto-calculate
        approvedAmount: null, // Will be set during approval
        priority: sanitizedData.priority || null,
        
        // Initialize clearance fields
        clearanceType: null,
        allocatedAmount: null,
        uoNumber: null,
        clearanceDate: null,
        clearanceStatus: CLEARANCE_STATUS.PENDING,
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        clearanceUpdatedAt: null,
        clearanceUpdatedBy: null,
        priorityUpdatedAt: null,
        priorityUpdatedBy: null
      };
      
      const docRef = await addDoc(collection(db, 'projects'), projectToSubmit);
      console.log('Project submitted with ID:', docRef.id);
      
      return { 
        success: true, 
        data: { id: docRef.id, ...projectToSubmit } 
      };
    } catch (error) {
      console.error('Error submitting project:', error);
      throw new Error('Failed to submit project. Please try again.');
    }
  },

  // Update project details (only for pending projects)
  async updateProject(projectId, updatedData, userId) {
    try {
      // Get current project
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }
      
      const currentProject = projectDoc.data();
      
      // Only allow editing pending projects
      if (currentProject.status !== PROJECT_STATUS.PENDING) {
        throw new Error('Only pending projects can be edited');
      }
      
      const sanitizedData = sanitizeProjectData(updatedData);
      
      // Prepare update data
      const updateData = {
        sector: sanitizedData.sector,
        department: sanitizedData.department,
        projectName: sanitizedData.projectName,
        financialRequirement: sanitizedData.financialRequirement,
        brief: sanitizedData.brief,
        priority: sanitizedData.priority,
        updatedAt: serverTimestamp(),
        lastModifiedBy: userId
      };
      
      await updateDoc(projectRef, updateData);
      
      console.log(`Project ${projectId} updated successfully`);
      return { 
        success: true, 
        data: { id: projectId, ...updateData },
        message: 'Project updated successfully'
      };
    } catch (error) {
      console.error('Error updating project:', error);
      throw new Error(error.message || 'Failed to update project. Please try again.');
    }
  },

  // Delete a project
  async deleteProject(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await deleteDoc(projectRef);
      
      console.log('Project deleted:', projectId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project: ' + error.message);
    }
  },

  // Get all projects (for admin approval page)
  async getAllProjects(filters = {}) {
    try {
      let projectsQuery = collection(db, 'projects');
      
      // Apply filters
      const constraints = [];
      
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      
      if (filters.sector) {
        constraints.push(where('sector', '==', filters.sector));
      }
      
      if (filters.department) {
        constraints.push(where('department', '==', filters.department));
      }
      
      if (filters.financialYear) {
        constraints.push(where('financialYear', '==', filters.financialYear));
      }
      
      // Apply sorting
      if (filters.sort) {
        const [sortField, sortOrder] = filters.sort.split('-');
        constraints.push(orderBy(sortField, sortOrder === 'desc' ? 'desc' : 'asc'));
      } else {
        // Default sort by creation date (newest first)
        constraints.push(orderBy('createdAt', 'desc'));
      }
      
      if (constraints.length > 0) {
        projectsQuery = query(projectsQuery, ...constraints);
      }
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        projects.push({
          id: doc.id,
          ...doc.data(),
          // Convert Firebase timestamps to Date objects
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          approvedAt: doc.data().approvedAt?.toDate(),
          clearanceDate: doc.data().clearanceDate?.toDate(),
          clearanceUpdatedAt: doc.data().clearanceUpdatedAt?.toDate(),
          priorityUpdatedAt: doc.data().priorityUpdatedAt?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Get projects by user (for department users to see their own projects)
  async getProjectsByUser(userId) {
    try {
      const projectsQuery = query(
        collection(db, 'projects'),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        projects.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          approvedAt: doc.data().approvedAt?.toDate(),
          clearanceDate: doc.data().clearanceDate?.toDate(),
          clearanceUpdatedAt: doc.data().clearanceUpdatedAt?.toDate(),
          priorityUpdatedAt: doc.data().priorityUpdatedAt?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching user projects:', error);
      throw new Error('Failed to load your projects. Please try again.');
    }
  },

  // Get approved projects for clearance (replaces clearanceService method)
  async getApprovedProjectsForClearance(financialYear = null) {
    try {
      let projectsQuery = query(
        collection(db, 'projects'),
        where('status', '==', PROJECT_STATUS.APPROVED),
        orderBy('department', 'asc')
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      let projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          projectName: data.projectName,
          department: data.department,
          sector: data.sector,
          financialRequirement: data.financialRequirement,
          approvedAmount: data.approvedAmount,
          financialYear: data.financialYear,
          clearanceType: data.clearanceType,
          allocatedAmount: data.allocatedAmount,
          uoNumber: data.uoNumber,
          clearanceDate: data.clearanceDate?.toDate(),
          clearanceStatus: data.clearanceStatus,
          priority: data.priority,
          createdAt: data.createdAt?.toDate(),
          approvedAt: data.approvedAt?.toDate(),
          clearanceUpdatedAt: data.clearanceUpdatedAt?.toDate(),
          priorityUpdatedAt: data.priorityUpdatedAt?.toDate()
        });
      });
      
      // Apply financial year filter if provided
      if (financialYear) {
        projects = projects.filter(project => project.financialYear === financialYear);
      }
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching approved projects:', error);
      throw new Error('Failed to load approved projects');
    }
  },

  // Update project status with approved amount (for approval process)
  async updateProjectStatusWithAmount(projectId, newStatus, approvedAmount, adminId) {
    try {
      // Validate the status change and approved amount - now mandatory for approval
      const validation = validateStatusChange(newStatus, approvedAmount);
      if (!validation.isValid) {
        throw new Error(Object.values(validation.errors)[0]);
      }

      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      // Handle approved status
      if (newStatus === PROJECT_STATUS.APPROVED) {
        // Approved amount is now MANDATORY for approval
        if (!approvedAmount || approvedAmount.toString().trim() === '') {
          throw new Error('Approved amount is required when approving a project');
        }
        
        // Additional validation for approved amount
        const numApprovedAmount = parseFloat(approvedAmount);
        if (isNaN(numApprovedAmount) || numApprovedAmount <= 0) {
          throw new Error('Approved amount must be a valid positive number');
        }
        
        if (numApprovedAmount > 99999) {
          throw new Error('Approved amount cannot exceed ₹99,999 lakhs');
        }
        
        updateData.approvedAmount = numApprovedAmount;
        updateData.approvedBy = adminId;
        updateData.approvedAt = serverTimestamp();
      } 
      // Handle rejected status
      else if (newStatus === PROJECT_STATUS.REJECTED) {
        updateData.approvedAmount = null;
        updateData.approvedBy = adminId;
        updateData.approvedAt = serverTimestamp();
      }

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, updateData);

      console.log(`Project ${projectId} status changed to ${newStatus} successfully`);
      return { success: true, data: { id: projectId, status: newStatus, ...updateData } };
    } catch (error) {
      console.error('Error updating project status with amount:', error);
      throw new Error(error.message || 'Failed to update project status. Please try again.');
    }
  },

  // Update clearance data for a project (replaces clearanceService.saveClearanceData)
  async updateClearanceData(projectId, clearanceData, userId) {
    try {
      // Validate clearance data
      const validation = validateClearanceData(clearanceData);
      if (!validation.isValid) {
        throw new Error(Object.values(validation.errors)[0]);
      }
      
      const sanitizedData = sanitizeClearanceData(clearanceData);
      
      // Prepare update data
      const updateData = {
        clearanceType: sanitizedData.clearanceType,
        allocatedAmount: sanitizedData.allocatedAmount,
        uoNumber: sanitizedData.uoNumber,
        clearanceDate: sanitizedData.clearanceDate,
        clearanceUpdatedAt: serverTimestamp(),
        clearanceUpdatedBy: userId,
        updatedAt: serverTimestamp()
      };
      
      // Remove null/undefined values for Firebase
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null || updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, updateData);
      
      console.log(`Clearance data updated for project ${projectId}`);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating clearance data:', error);
      throw new Error('Failed to save clearance data. Please try again.');
    }
  },

  // Get project by ID
  async getProjectById(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }
      
      const projectData = projectDoc.data();
      return {
        success: true,
        data: {
          id: projectDoc.id,
          ...projectData,
          createdAt: projectData.createdAt?.toDate(),
          updatedAt: projectData.updatedAt?.toDate(),
          approvedAt: projectData.approvedAt?.toDate(),
          clearanceDate: projectData.clearanceDate?.toDate(),
          clearanceUpdatedAt: projectData.clearanceUpdatedAt?.toDate(),
          priorityUpdatedAt: projectData.priorityUpdatedAt?.toDate()
        }
      };
    } catch (error) {
      console.error('Error fetching project by ID:', error);
      throw new Error('Failed to load project. Please try again.');
    }
  },

  // Update only the approved amount (for editing after approval)
  async updateApprovedAmount(projectId, newApprovedAmount, adminId) {
    try {
      // Get current project data
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }
      
      const projectData = projectDoc.data();
      
      // Only allow updating approved amount for approved projects
      if (projectData.status !== PROJECT_STATUS.APPROVED) {
        throw new Error('Can only update approved amount for approved projects');
      }

      // Validate the new approved amount
      const validation = validateStatusChange(PROJECT_STATUS.APPROVED, newApprovedAmount);
      if (!validation.isValid) {
        throw new Error(Object.values(validation.errors)[0]);
      }

      const updateData = {
        approvedAmount: parseFloat(newApprovedAmount),
        updatedAt: serverTimestamp(),
        lastModifiedBy: adminId
      };

      await updateDoc(projectRef, updateData);

      console.log(`Project ${projectId} approved amount updated successfully`);
      return { success: true, data: { id: projectId, ...updateData } };
    } catch (error) {
      console.error('Error updating approved amount:', error);
      throw new Error(error.message || 'Failed to update approved amount. Please try again.');
    }
  },

  // Update project priority
  async updateProjectPriority(projectId, priority, userId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const updateData = {
        priority: priority !== null && priority !== '' ? parseInt(priority) : null,
        priorityUpdatedAt: serverTimestamp(),
        priorityUpdatedBy: userId,
        updatedAt: serverTimestamp()
      };

      await updateDoc(projectRef, updateData);

      console.log(`Project ${projectId} priority updated to ${priority}`);
      return { 
        success: true, 
        data: { id: projectId, ...updateData } 
      };
    } catch (error) {
      console.error('Error updating project priority:', error);
      throw new Error(error.message || 'Failed to update project priority. Please try again.');
    }
  },

  // Get project statistics (for dashboard or analytics)
  async getProjectStatistics() {
    try {
      const querySnapshot = await getDocs(collection(db, 'projects'));
      
      const stats = {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        totalRequestedAmount: 0,
        totalApprovedAmount: 0,
        totalAllocatedAmount: 0,
        withPriority: 0,
        bySector: {},
        byDepartment: {},
        byFinancialYear: {},
        byClearanceType: {},
        approvalRate: 0,
        clearanceRate: 0
      };
      
      querySnapshot.forEach((doc) => {
        const project = doc.data();
        
        stats.total++;
        stats[project.status] = (stats[project.status] || 0) + 1;
        
        // Count projects with priority
        if (project.priority) {
          stats.withPriority++;
        }
        
        // Financial amounts
        if (project.financialRequirement) {
          stats.totalRequestedAmount += project.financialRequirement;
        }
        
        if (project.approvedAmount) {
          stats.totalApprovedAmount += project.approvedAmount;
        }
        
        if (project.allocatedAmount) {
          stats.totalAllocatedAmount += project.allocatedAmount;
        }
        
        // Group by sector
        if (project.sector) {
          if (!stats.bySector[project.sector]) {
            stats.bySector[project.sector] = { 
              count: 0, 
              requestedAmount: 0, 
              approvedAmount: 0,
              allocatedAmount: 0
            };
          }
          stats.bySector[project.sector].count++;
          if (project.financialRequirement) {
            stats.bySector[project.sector].requestedAmount += project.financialRequirement;
          }
          if (project.approvedAmount) {
            stats.bySector[project.sector].approvedAmount += project.approvedAmount;
          }
          if (project.allocatedAmount) {
            stats.bySector[project.sector].allocatedAmount += project.allocatedAmount;
          }
        }
        
        // Group by department
        if (project.department) {
          if (!stats.byDepartment[project.department]) {
            stats.byDepartment[project.department] = { 
              count: 0, 
              requestedAmount: 0, 
              approvedAmount: 0,
              allocatedAmount: 0
            };
          }
          stats.byDepartment[project.department].count++;
          if (project.financialRequirement) {
            stats.byDepartment[project.department].requestedAmount += project.financialRequirement;
          }
          if (project.approvedAmount) {
            stats.byDepartment[project.department].approvedAmount += project.approvedAmount;
          }
          if (project.allocatedAmount) {
            stats.byDepartment[project.department].allocatedAmount += project.allocatedAmount;
          }
        }
        
        // Group by financial year
        if (project.financialYear) {
          if (!stats.byFinancialYear[project.financialYear]) {
            stats.byFinancialYear[project.financialYear] = { 
              count: 0, 
              requestedAmount: 0, 
              approvedAmount: 0,
              allocatedAmount: 0
            };
          }
          stats.byFinancialYear[project.financialYear].count++;
          if (project.financialRequirement) {
            stats.byFinancialYear[project.financialYear].requestedAmount += project.financialRequirement;
          }
          if (project.approvedAmount) {
            stats.byFinancialYear[project.financialYear].approvedAmount += project.approvedAmount;
          }
          if (project.allocatedAmount) {
            stats.byFinancialYear[project.financialYear].allocatedAmount += project.allocatedAmount;
          }
        }
        
        // Group by clearance type
        if (project.clearanceType) {
          if (!stats.byClearanceType[project.clearanceType]) {
            stats.byClearanceType[project.clearanceType] = { 
              count: 0, 
              allocatedAmount: 0
            };
          }
          stats.byClearanceType[project.clearanceType].count++;
          if (project.allocatedAmount) {
            stats.byClearanceType[project.clearanceType].allocatedAmount += project.allocatedAmount;
          }
        }
      });
      
      // Calculate approval rate
      if (stats.total > 0) {
        stats.approvalRate = ((stats.approved || 0) / stats.total * 100).toFixed(1);
      }
      
      // Calculate clearance rate (projects with clearance data vs approved projects)
      const approvedCount = stats.approved || 0;
      const clearedCount = Object.values(stats.byClearanceType).reduce((sum, type) => sum + type.count, 0);
      if (approvedCount > 0) {
        stats.clearanceRate = ((clearedCount / approvedCount) * 100).toFixed(1);
      }
      
      return { success: true, data: stats };
    } catch (error) {
      console.error('Error fetching project statistics:', error);
      throw new Error('Failed to load project statistics.');
    }
  },

  // Get projects by financial year
  async getProjectsByFinancialYear(financialYear) {
    try {
      const projectsQuery = query(
        collection(db, 'projects'),
        where('financialYear', '==', financialYear),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        projects.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          approvedAt: doc.data().approvedAt?.toDate(),
          clearanceDate: doc.data().clearanceDate?.toDate(),
          clearanceUpdatedAt: doc.data().clearanceUpdatedAt?.toDate(),
          priorityUpdatedAt: doc.data().priorityUpdatedAt?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching projects by financial year:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Legacy method for backward compatibility
  async updateProjectStatus(projectId, action, adminId) {
    try {
      const statusMap = {
        [PROJECT_ACTIONS.APPROVE]: PROJECT_STATUS.APPROVED,
        [PROJECT_ACTIONS.REJECT]: PROJECT_STATUS.REJECTED
      };
      
      const newStatus = statusMap[action];
      if (!newStatus) {
        throw new Error('Invalid action');
      }
      
      // For legacy compatibility, use the financial requirement as approved amount
      const projectDoc = await getDoc(doc(db, 'projects', projectId));
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }
      
      const projectData = projectDoc.data();
      const approvedAmount = action === PROJECT_ACTIONS.APPROVE ? projectData.financialRequirement : null;
      
      return await this.updateProjectStatusWithAmount(projectId, newStatus, approvedAmount, adminId);
    } catch (error) {
      console.error('Error updating project status:', error);
      throw new Error(`Failed to ${action} project. Please try again.`);
    }
  },

  // Bulk operations for admin efficiency
  async bulkUpdateProjectStatus(projectIds, newStatus, adminId) {
    try {
      const results = {
        successful: [],
        failed: []
      };
      
      for (const projectId of projectIds) {
        try {
          const result = await this.updateProjectStatusWithAmount(projectId, newStatus, null, adminId);
          if (result.success) {
            results.successful.push(projectId);
          } else {
            results.failed.push({ projectId, error: 'Update failed' });
          }
        } catch (error) {
          results.failed.push({ projectId, error: error.message });
        }
      }
      
      return {
        success: true,
        data: results
      };
    } catch (error) {
      console.error('Error in bulk update:', error);
      throw new Error('Failed to perform bulk update.');
    }
  },

  // Advanced filtering for complex queries
  async getProjectsWithAdvancedFilters(filters) {
    try {
      // This method can be expanded for more complex filtering needs
      const allProjects = await this.getAllProjects();
      let filteredProjects = allProjects.data;
      
      // Additional client-side filtering for complex conditions
      if (filters.amountRange) {
        const { min, max } = filters.amountRange;
        filteredProjects = filteredProjects.filter(project => {
          const amount = project.financialRequirement || 0;
          return amount >= min && amount <= max;
        });
      }
      
      if (filters.dateRange) {
        const { startDate, endDate } = filters.dateRange;
        filteredProjects = filteredProjects.filter(project => {
          const projectDate = project.createdAt;
          return projectDate >= startDate && projectDate <= endDate;
        });
      }
      
      if (filters.hasClearanceData !== undefined) {
        filteredProjects = filteredProjects.filter(project => {
          const hasClearance = !!(project.clearanceType || project.allocatedAmount || project.uoNumber);
          return hasClearance === filters.hasClearanceData;
        });
      }
      
      // Filter by priority
      if (filters.hasPriority !== undefined) {
        filteredProjects = filteredProjects.filter(project => {
          const hasPriority = project.priority !== null && project.priority !== undefined;
          return hasPriority === filters.hasPriority;
        });
      }
      
      if (filters.priorityRange) {
        const { min, max } = filters.priorityRange;
        filteredProjects = filteredProjects.filter(project => {
          const priority = project.priority || 999999;
          return priority >= min && priority <= max;
        });
      }
      
      return {
        success: true,
        data: filteredProjects
      };
    } catch (error) {
      console.error('Error in advanced filtering:', error);
      throw new Error('Failed to perform advanced filtering.');
    }
  }
};

export default projectService;